import React from 'react';
import { NumericFormat } from 'react-number-format';
import { dinero, toDecimal, toUnits } from 'dinero.js';
import cx from 'classnames';

import { getCurrencySymbol, getDineroCurrency } from 'utils/currency';

interface CurrencyInputProps {
  max?: number;
  withLabel?: boolean;
  id?: string;
  currencyCode: string;
  name?: string;
  required?: boolean;
  inputClassName?: string;
  showCurrencyPrefix?: boolean;
  showCurrencySuffix?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  ref?: any;
  placeholder?: string;
  showCurrencySymbol?: boolean;
  value?: number;
  onChange?: (dineroAmount: number) => void;
  onBlur?: (dineroAmount: number) => void;
}

const getCurrencyValue = (amount: number, currencyCode: string) => {
  // if amount is 0, undefined, or null, or not an integer return empty string
  if (!amount || !currencyCode || amount === 0 || !Number.isInteger(amount)) {
    return '';
  }

  const dineroCurrency = getDineroCurrency(currencyCode);
  const dineroValue = dinero({
    amount,
    currency: dineroCurrency,
  });
  const exponent = dineroCurrency.exponent;
  return exponent === 2
    ? toDecimal(dineroValue, ({ value }) => `${value}`)
    : toUnits(dineroValue, ({ value }) => `${value[0]}`);
};

const CurrencyInput = React.forwardRef<any, CurrencyInputProps>(
  (
    {
      withLabel,
      max,
      currencyCode,
      id = 'customAmount',
      name,
      placeholder = '00.00',
      required,
      disabled,
      autoFocus,
      inputClassName,
      showCurrencyPrefix,
      showCurrencySuffix,
      showCurrencySymbol,
      value,
      onChange,
      onBlur,
    },
    ref,
  ) => {
    const className = cx(
      'block w-full rounded-lg border border-neutral-100 bg-neutral-100 px-2.5 py-2 text-base text-neutral-900 focus:border focus:border-blue-500 focus:ring-blue-500',
      inputClassName,
      showCurrencyPrefix ? 'text-right' : '',
    );

    const dineroCurrency = getDineroCurrency(currencyCode);
    const renderedValue = getCurrencyValue(value, currencyCode);

    return (
      <>
        {withLabel && (
          <label htmlFor={id} className={`form-label ${required ? 'required' : ''}`}>
            {`Enter an amount (${getCurrencySymbol(currencyCode)})`}
          </label>
        )}
        <div className="number-input-wrapper relative">
          <NumericFormat
            value={renderedValue}
            className={className}
            allowNegative={false}
            thousandSeparator=","
            fixedDecimalScale
            allowLeadingZeros={false}
            placeholder={placeholder}
            decimalScale={dineroCurrency.exponent}
            onValueChange={({ formattedValue }) => {
              // if formattedValue is not an empty string return numeric value otherwise return an empty string, 0 is a value and an empty string represents uninitialized or reset values
              const dineroValue = formattedValue === '' ? null : Number(formattedValue.replace(/\D+/g, ''));
              onChange(dineroValue);
            }}
            onBlur={
              onBlur
                ? (event) => {
                    const formattedValue = event.target.value;
                    // if formattedValue is not an empty string return numeric value otherwise return an empty string, 0 is a value and an empty string represents uninitialized or reset values
                    const dineroValue = formattedValue === '' ? null : Number(formattedValue.replace(/\D+/g, ''));
                    onBlur(dineroValue);
                  }
                : undefined
            }
            prefix={showCurrencySymbol ? getCurrencySymbol(currencyCode) : ''}
            disabled={disabled}
            id={id}
            name={name}
            getInputRef={ref}
            autoFocus={autoFocus}
            isAllowed={({ formattedValue }) => {
              const dineroValue = Number(formattedValue.replace(/\D+/g, '')); // remove everything except numbers
              if (max) {
                return dineroValue <= max;
              }
              return true;
            }}
          />
          {showCurrencySuffix && <span className="absolute right-2.5 top-2.5">{currencyCode}</span>}
          {showCurrencyPrefix && (
            <span className="text-normal absolute left-2.5 top-2.5 text-neutral-500">
              {getCurrencySymbol(currencyCode)}
            </span>
          )}
        </div>
      </>
    );
  },
);

export default CurrencyInput;
